/**
 * The one place the "Vera " plan-name prefix is stripped for in-app display.
 *
 * Stripe product names carry the prefix — "Vera Starter", "Vera Business" — and they
 * should: they appear on invoices, receipts and card statements, read OUTSIDE the app,
 * where a bare "Enterprise — $999.00" is ambiguous about whose product it is. Inside the
 * platform the user is already in Vera, so the prefix is noise on every render.
 *
 * Applied at the BOUNDARIES where plan data enters the client (`subscriptionApi`,
 * `useCompaniesQuery`), never at a render site. There are at least four render sites —
 * the sidebar chip, Settings → Billing's Plan card, the internal Companies list, the
 * company-detail Subscription panel — and when only one of them stripped, the sidebar
 * read "Premium Plan" while the Billing card read "Vera Premium Plan": two names for one
 * plan, on one account, on one screen. A single choke point cannot drift that way.
 *
 * WHY THIS CANNOT BE RETIRED BY THE CATALOG SYNC. `sync-stripe-catalog` writes bare
 * titles via its own `normalizePlanTitle`, which is the right layer for anything it
 * touches — but it only updates rows with a matching ACTIVE Stripe price. Rows with no
 * such price are DEACTIVATED and their titles left alone. So any company on a retired
 * plan keeps a prefixed title indefinitely, and a sync-boundary fix alone would never
 * reach exactly the companies most likely to look wrong.
 *
 * Strips only a leading "Vera " and passes anything else through untouched, so a plan
 * that never carried the prefix is not mangled.
 */
export const stripVeraPlanPrefix = (name?: string | null): string | undefined =>
  name?.replace(/^Vera\s+/i, '') || undefined;

/** Object-level convenience: returns a copy with `title` stripped. */
export const stripPlanTitle = <T extends {title?: string | null}>(plan: T): T =>
  plan?.title ? {...plan, title: stripVeraPlanPrefix(plan.title) ?? plan.title} : plan;

/** Object-level convenience for payloads that use `name` instead of `title`. */
export const stripPlanName = <T extends {name?: string | null}>(plan: T): T =>
  plan?.name ? {...plan, name: stripVeraPlanPrefix(plan.name) ?? plan.name} : plan;
